import React from 'react';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

export function MarkdownRenderer({ content, className = "" }: MarkdownRendererProps) {
  const parseMarkdown = (text: string): React.ReactNode[] => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let currentList: React.ReactNode[] = [];
    let listType: 'ul' | 'ol' | null = null;
    
    const flushList = () => {
      if (currentList.length > 0 && listType) {
        const ListComponent = listType;
        const listClassName = listType === 'ul' 
          ? "ml-2 sm:ml-4 mb-3 space-y-1 list-disc list-inside" 
          : "ml-2 sm:ml-4 mb-3 space-y-1 list-decimal list-inside";
        elements.push(
          <ListComponent key={`list-${elements.length}`} className={listClassName}>
            {currentList}
          </ListComponent>
        );
        currentList = [];
        listType = null;
      }
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      // Skip empty lines
      if (!trimmedLine) {
        flushList();
        elements.push(<br key={`br-${index}`} />);
        return;
      }

      // Headers
      if (trimmedLine.startsWith('###')) {
        flushList();
        const text = trimmedLine.replace(/^###\s*/, '');
        elements.push(
          <h3 key={`h3-${index}`} className="text-lg font-semibold mb-2 mt-4">
            {parseInlineMarkdown(text)}
          </h3>
        );
        return;
      }
      
      if (trimmedLine.startsWith('##')) {
        flushList();
        const text = trimmedLine.replace(/^##\s*/, '');
        elements.push(
          <h2 key={`h2-${index}`} className="text-xl font-bold mb-3 mt-4">
            {parseInlineMarkdown(text)}
          </h2>
        );
        return;
      }
      
      if (trimmedLine.startsWith('#')) {
        flushList();
        const text = trimmedLine.replace(/^#\s*/, '');
        elements.push(
          <h1 key={`h1-${index}`} className="text-2xl font-bold mb-3 mt-4">
            {parseInlineMarkdown(text)}
          </h1>
        );
        return;
      }

      // Unordered lists
      if (trimmedLine.match(/^[-*+]\s/)) {
        if (listType !== 'ul') {
          flushList();
          listType = 'ul';
        }
        const text = trimmedLine.replace(/^[-*+]\s*/, '');
        currentList.push(
          <li key={`li-${index}`} className="text-sm pl-1">
            {parseInlineMarkdown(text)}
          </li>
        );
        return;
      }

      // Ordered lists
      if (trimmedLine.match(/^\d+\.\s/)) {
        if (listType !== 'ol') {
          flushList();
          listType = 'ol';
        }
        const text = trimmedLine.replace(/^\d+\.\s*/, '');
        currentList.push(
          <li key={`li-${index}`} className="text-sm pl-1">
            {parseInlineMarkdown(text)}
          </li>
        );
        return;
      }

      // Code blocks (simple detection)
      if (trimmedLine.startsWith('```')) {
        flushList();
        // For now, just render as preformatted text
        const codeContent = trimmedLine.replace(/^```\w*\s*/, '').replace(/```$/, '');
        if (codeContent) {
          elements.push(
            <pre key={`code-${index}`} className="bg-gray-800 text-green-400 p-3 rounded text-xs overflow-x-auto mb-3 font-mono">
              <code>{codeContent}</code>
            </pre>
          );
        }
        return;
      }

      // Regular paragraphs
      flushList();
      elements.push(
        <p key={`p-${index}`} className="mb-2 leading-relaxed">
          {parseInlineMarkdown(trimmedLine)}
        </p>
      );
    });

    // Flush any remaining list
    flushList();

    return elements;
  };

  const parseInlineMarkdown = (text: string): React.ReactNode[] => {
    const elements: React.ReactNode[] = [];
    let currentText = text;
    let keyCounter = 0;

    // Bold text **text**
    currentText = currentText.replace(/\*\*(.*?)\*\*/g, (match, content) => {
      const placeholder = `__BOLD_${keyCounter}__`;
      elements.push(
        <strong key={`bold-${keyCounter}`} className="font-semibold">
          {content}
        </strong>
      );
      keyCounter++;
      return placeholder;
    });

    // Italic text *text*
    currentText = currentText.replace(/\*(.*?)\*/g, (match, content) => {
      const placeholder = `__ITALIC_${keyCounter}__`;
      elements.push(
        <em key={`italic-${keyCounter}`} className="italic">
          {content}
        </em>
      );
      keyCounter++;
      return placeholder;
    });

    // Inline code `code`
    currentText = currentText.replace(/`(.*?)`/g, (match, content) => {
      const placeholder = `__CODE_${keyCounter}__`;
      elements.push(
        <code key={`code-${keyCounter}`} className="bg-gray-200 px-1 py-0.5 rounded text-xs font-mono">
          {content}
        </code>
      );
      keyCounter++;
      return placeholder;
    });

    // Links [text](url)
    currentText = currentText.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
      const placeholder = `__LINK_${keyCounter}__`;
      elements.push(
        <a 
          key={`link-${keyCounter}`} 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          {text}
        </a>
      );
      keyCounter++;
      return placeholder;
    });

    // Split text by placeholders and reconstruct
    const parts = currentText.split(/(__(?:BOLD|ITALIC|CODE|LINK)_\d+__)/);
    const result: React.ReactNode[] = [];
    
    parts.forEach((part, index) => {
      if (part.startsWith('__') && part.endsWith('__')) {
        // Find the corresponding element
        const elementIndex = parseInt(part.match(/_(\d+)__$/)?.[1] || '0');
        const element = elements.find((_, i) => i === elementIndex);
        if (element) {
          result.push(element);
        }
      } else if (part) {
        result.push(part);
      }
    });

    return result.length > 0 ? result : [text];
  };

  return (
    <div className={`markdown-content ${className}`}>
      {parseMarkdown(content)}
    </div>
  );
}
